/**
 * The alerts control: a speaker that mutes the chime, and — only while the
 * browser has not been asked yet — an offer of desktop notifications.
 *
 * It lives on the Live Chats header rather than in a global bell because that is
 * where somebody is when they think about being interrupted by chats, and a
 * portal-wide notification surface is exactly what `useChatQueueStore` declined
 * to add. Two small controls in the page they belong to, not a third inbox.
 *
 * The permission ask is attached to a real click on purpose: browsers throttle
 * or silently swallow `Notification.requestPermission()` outside a user gesture,
 * and asking on mount is how a portal ends up permanently blocked.
 */
import { useState } from 'react';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import { Button, useToast } from '@respectly/ui';
import { useChatQueueStore } from '../../stores/useChatQueueStore';
import {
  notificationPermission,
  requestNotificationPermission,
  type NotificationPermissionState,
} from '../../lib/desktopNotification';

export function AlertToggle() {
  const soundEnabled = useChatQueueStore((s) => s.soundEnabled);
  const setSoundEnabled = useChatQueueStore((s) => s.setSoundEnabled);
  const { toast } = useToast();
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    notificationPermission()
  );

  const askForDesktopAlerts = async (): Promise<void> => {
    const result = await requestNotificationPermission();
    setPermission(result);
    if (result === 'granted') {
      toast({
        title: 'Desktop alerts on',
        description: "You'll be told about new chats even when this tab is in the background.",
        variant: 'success',
      });
    } else if (result === 'denied') {
      // Say where it went. A browser-level block cannot be undone from here, and
      // a control that silently does nothing next time is worse than the refusal.
      toast({
        title: 'Desktop alerts blocked',
        description: 'Your browser denied notifications for this site. Change it in site settings.',
        variant: 'warning',
      });
    }
  };

  return (
    <>
      {permission === 'default' && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void askForDesktopAlerts()}
          className="h-8 gap-1.5 rounded-lg px-2 text-xs font-medium text-slate-500"
          title="Get notified about new chats when this tab is in the background"
        >
          <Bell className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Desktop alerts</span>
        </Button>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={() => setSoundEnabled(!soundEnabled)}
        className="h-8 w-8 rounded-lg p-0"
        aria-pressed={soundEnabled}
        title={soundEnabled ? 'Mute the new-chat sound' : 'Play a sound for new chats'}
      >
        {soundEnabled ? (
          <Volume2 className="h-3.5 w-3.5 text-teal-600 dark:text-teal-400" />
        ) : (
          <VolumeX className="h-3.5 w-3.5 text-slate-400" />
        )}
        <span className="sr-only">
          {soundEnabled ? 'Mute the new-chat sound' : 'Play a sound for new chats'}
        </span>
      </Button>
    </>
  );
}
